/**
 * Push Notification Delivery — polls get_pending_notifications() and sends via Expo Push API.
 * Can be called from an edge function or a client-side admin trigger.
 */

import { createLogger } from '@/lib/logger';

const log = createLogger('PUSH_DELIVERY');

const EXPO_PUSH_URL = 'https://exp.host/--/api/v2/push/send';
const BATCH_SIZE = 100;

interface PushMessage {
  to: string;
  title: string;
  body: string;
  data?: Record<string, any>;
  sound?: 'default';
  badge?: number;
}

interface PendingNotification {
  notification_id: string;
  user_id: string;
  type: string;
  title: string;
  body: string;
  data: Record<string, any>;
  tokens: string[];
}

async function sendBatch(messages: PushMessage[]): Promise<{ sent: number; failed: number }> {
  let sent = 0;
  let failed = 0;

  try {
    const response = await fetch(EXPO_PUSH_URL, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Accept-Encoding': 'gzip, deflate',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(messages),
    });

    if (!response.ok) {
      log.error('Expo Push API error', { status: response.status });
      return { sent: 0, failed: messages.length };
    }

    const result = await response.json();
    const data = result.data ?? [];

    for (const ticket of data) {
      if (ticket.status === 'ok') {
        sent++;
      } else {
        failed++;
        log.warn('Push ticket error', { detail: ticket.details?.error });
      }
    }
  } catch (error) {
    log.error('Failed to send push batch', error);
    failed += messages.length;
  }

  return { sent, failed };
}

export async function deliverPendingNotifications(
  supabaseClient: any,
): Promise<{ sent: number; failed: number }> {
  let totalSent = 0;
  let totalFailed = 0;

  try {
    // Call get_pending_notifications RPC
    const { data: notifications, error } = await supabaseClient.rpc(
      'get_pending_notifications',
      { batch_size: BATCH_SIZE },
    );

    if (error) {
      log.error('Failed to fetch pending notifications', error);
      return { sent: 0, failed: 0 };
    }

    if (!notifications || notifications.length === 0) {
      log.info('No pending notifications');
      return { sent: 0, failed: 0 };
    }

    // Map to Expo push messages — one message per token
    const messages: PushMessage[] = [];
    for (const notif of notifications as PendingNotification[]) {
      for (const token of notif.tokens) {
        messages.push({
          to: token,
          title: notif.title,
          body: notif.body,
          data: { ...notif.data, type: notif.type, notification_id: notif.notification_id },
          sound: 'default',
        });
      }
    }

    // Send in batches of 100
    for (let i = 0; i < messages.length; i += BATCH_SIZE) {
      const batch = messages.slice(i, i + BATCH_SIZE);
      const result = await sendBatch(batch);
      totalSent += result.sent;
      totalFailed += result.failed;
    }

    log.info(`Delivered ${totalSent} notifications, ${totalFailed} failed`);
  } catch (error) {
    log.error('deliverPendingNotifications error', error);
  }

  return { sent: totalSent, failed: totalFailed };
}
